import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  canResumePipeline,
  pipelineStatusAfterStep,
  resolveResumeStep,
  resumeStepMeta,
  runTutorialStep,
  stepsFrom,
} from '@/lib/learningTutorial/pipelineRunner';
import { pipelineProgressPct } from '@/lib/learningTutorial/pipelineSteps';
import { tutorialUiStateFromProject } from '@/lib/learningTutorial/tutorialHistory';

/**
 * Shared pipeline outline + resume/retry controls for tutorial flow and editor.
 */
export function useTutorialPipelineRunner({
  api,
  projectId,
  project,
  setProject,
  onProjectRefreshed,
}) {
  const [orchestration, setOrchestration] = useState(null);
  const [status, setStatus] = useState('idle');
  const [currentStep, setCurrentStep] = useState(null);
  const [outlineOpen, setOutlineOpen] = useState(false);
  const [pipelineError, setPipelineError] = useState(null);

  useEffect(() => {
    if (!project || status === 'working') return;
    setOrchestration(project.orchestration || null);
    const ui = tutorialUiStateFromProject(project);
    setCurrentStep(ui.currentStep);
    setStatus(ui.status);
    setPipelineError(
      ui.status === 'error' ? (project.last_pipeline_error || null) : null,
    );
  }, [project, status]);

  const fetchProject = useCallback(async (pid) => {
    const { data } = await api.get(`/learning-tutorials/projects/${pid}`);
    const full = data.project;
    setProject(full);
    onProjectRefreshed?.(full);
    return full;
  }, [api, setProject, onProjectRefreshed]);

  const runSteps = useCallback(async (pid, stepIds) => {
    let lastOrch = null;
    let lastProject = null;
    for (const stepId of stepIds) {
      setCurrentStep(stepId);
      setOutlineOpen(true);
      if (stepId === 'pass2') {
        toast.message('Pass 2 — expanding artifacts (LLM). Large topics may take several minutes…');
      } else if (stepId === 'pass2_optimize') {
        toast.message('Pass 2 optimize — deduplicating topics and filling gaps…');
      } else if (stepId === 'script') {
        toast.message('Pass 3 — writing trainer script (LLM)…');
      } else if (stepId === 'narration_audio') {
        toast.message('Generating trainer narration audio (TTS)…');
      } else if (stepId === 'word_align') {
        toast.message('Running WhisperX word alignment…');
      } else if (stepId === 'timeline') {
        toast.message('Finalizing timeline with measured audio clocks…');
      } else if (stepId === 'artifact_embed') {
        toast.message('Embedding artifact render times from trainer script…');
      }
      const result = await runTutorialStep(api, pid, stepId);
      if (result.orchestration) {
        lastOrch = result.orchestration;
        setOrchestration(result.orchestration);
      }
      if (result.project) {
        lastProject = result.project;
        setProject(result.project);
        onProjectRefreshed?.(result.project);
      } else if (
        stepId === 'timeline'
        || stepId === 'script'
        || stepId === 'narration_audio'
        || stepId === 'word_align'
        || stepId === 'artifact_embed'
      ) {
        lastProject = await fetchProject(pid);
      }
      if (result.toastMessage) {
        toast.success(result.toastMessage);
      }
    }
    return {
      orchestration: lastOrch,
      project: lastProject || (await fetchProject(pid)),
    };
  }, [api, fetchProject, setProject, onProjectRefreshed]);

  const executeResume = useCallback(async (stepId, { continueAfter = false } = {}) => {
    if (!projectId || !stepId || status === 'working') return;

    setPipelineError(null);
    setStatus('working');
    setOutlineOpen(true);
    setCurrentStep(stepId);

    const stepIds = continueAfter ? stepsFrom(stepId) : [stepId];
    try {
      const { orchestration: orch, project: proj } = await runSteps(projectId, stepIds);
      const lastStep = stepIds[stepIds.length - 1];
      const finalOrch = orch || proj?.orchestration || orchestration;
      if (finalOrch && !orch) setOrchestration(finalOrch);

      if (continueAfter && lastStep === 'artifact_embed') {
        setCurrentStep('done');
        setStatus('ready');
        setPipelineError(null);
        toast.success('Pipeline complete');
        return;
      }

      const next = pipelineStatusAfterStep(lastStep, proj, finalOrch);
      setCurrentStep(next.currentStep);
      setStatus(next.status);
      if (next.status === 'ready') {
        toast.success('Pipeline complete');
      } else {
        toast.success(`${resumeStepMeta(stepId).shortLabel} step completed`);
      }
    } catch (err) {
      const isTimeout = err.code === 'ECONNABORTED' || /timeout/i.test(String(err.message || ''));
      let message = err.response?.data?.detail || err.message || 'Tutorial pipeline failed';
      if (isTimeout && (stepId === 'narration_audio' || stepId === 'word_align' || stepId === 'artifact_embed')) {
        message = 'Narration step timed out in the browser — the server may still be working. Wait, then retry this step.';
      } else if (isTimeout && (stepId === 'pass1' || stepId === 'pass2' || stepId === 'pass2_optimize' || stepId === 'script')) {
        message = `${resumeStepMeta(stepId).shortLabel} timed out in the browser — the server may still be working. Wait a minute, then retry this step.`;
      }
      setStatus('error');
      setPipelineError(message);
      setCurrentStep(stepId);
      toast.error(message);
    }
  }, [projectId, status, runSteps, orchestration]);

  const retryStep = useCallback(() => {
    const stepId = resolveResumeStep({ project, currentStepId: currentStep, pipelineStatus: status });
    if (stepId) executeResume(stepId, { continueAfter: false });
  }, [project, currentStep, status, executeResume]);

  const retryAndContinue = useCallback(() => {
    const stepId = resolveResumeStep({ project, currentStepId: currentStep, pipelineStatus: status });
    if (stepId) executeResume(stepId, { continueAfter: true });
  }, [project, currentStep, status, executeResume]);

  const runPipelineStep = useCallback(
    (stepId) => executeResume(stepId, { continueAfter: false }),
    [executeResume],
  );

  const resumeStepId = useMemo(
    () => resolveResumeStep({ project, currentStepId: currentStep, pipelineStatus: status }),
    [project, currentStep, status],
  );

  const showResumeActions = useMemo(
    () => canResumePipeline({
      projectId,
      project,
      pipelineStatus: status,
      currentStepId: currentStep,
      orchestration,
    }),
    [projectId, project, status, currentStep, orchestration],
  );

  const progressPct = useMemo(
    () => pipelineProgressPct(currentStep, status),
    [currentStep, status],
  );

  const hasPipeline = Boolean(orchestration || project?.timeline || project?.script);

  return {
    orchestration,
    status,
    currentStep,
    outlineOpen,
    setOutlineOpen,
    pipelineError,
    resumeStepId,
    showResumeActions,
    progressPct,
    hasPipeline,
    retryStep,
    retryAndContinue,
    runPipelineStep,
    executeResume,
  };
}
